import { inject } from '@angular/core';
import { Router, CanActivateFn, ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { UnitDTO } from '@class/commons/unit.model';
import { UnitsService } from '@service/units/units.service';
import { DropletV4 } from '@utils/droplet-v4';

/**
 * Guard to handle navigation between V4 and non-V4 routes based on the unit type,
 * redirecting to the appropriate route if necessary.
 *
 * Because we need to fetch the unit details here to determine if the unit is V4 or not,
 * using this guard will result in 2 calls to the unit details API endpoint: once here,
 * and once again when the unit details page is rendered. This is not ideal but it is
 * also not an issue. We can improve this in the future by using a resolver or caching
 * the unit details in the store.
 *
 * For development purposes we can also use localStorage to determine if the unit is V4.
 *
 * @example
 * // Manually mark an endpoint as V4 for testing:
 * window.localStorage.setItem('v4Endpoints', JSON.stringify(['unit1', 'unit2']))}
 *
 * @param route - The activated route snapshot.
 * @returns - True if the navigation is allowed, false otherwise.
 */
export const V4UnitGuard: CanActivateFn = async (route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const unitService = inject(UnitsService);
  const unitDetailsResponse = await unitService.getUnitDetails(route.params.id);
  const unit = unitDetailsResponse.data as UnitDTO;

  const isV4Unit = unit.is_v4_enabled || DropletV4.isForcedV4(route.params.id);
  const navigatingToV4Route = route.routeConfig?.path.indexOf('/v4/') > -1;

  // If this is a V4 unit and we are not navigating to a V4 route, redirect to the V4 route.
  if (isV4Unit && !navigatingToV4Route) {
    router.navigate(['/unit/v4/' + route.params.id]);
    return false;
  }

  // If this is NOT a V4 unit and we ARE navigating to a V4 route, redirect to the non-V4 route.
  if (!isV4Unit && navigatingToV4Route) {
    router.navigate(['/unit/' + route.params.id]);
    return false;
  }

  return true;
};
